// useUserLevelTimeRelatedData.js
import { useEffect, useState, useMemo } from 'react';
import { fromUnixTime, isWithinInterval, isSameMonth, subDays } from 'date-fns';
import { useSelector } from 'react-redux';

import getMeData from '../selector/getMeData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { VIP_LEVEL_INFORM_EXPIRE_SOON_DAYS } from '../RemoteConfigKeys.js';

import { EMPTY_OBJECT } from '../resource/defaults.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import getCurrentUserLevel from '../resource/getCurrentUserLevel.js';
import {
  getVipBenefitLevelThresholdByUserLevel,
  getUserLevelBenefitThresholdList,
} from '../selector/getUserLevelBenefitData.js';

const INTERVAL_TIME = 60 * 1000;

/**
 * A custom React hook that provides time-related data for the user's level.
 *
 * @returns {Object} An object containing:
 *   - {number} level - Current user level
 *   - {number} expireUnixTimestamp - Unix timestamp of level expiration
 *   - {number} startUnixTimestamp - Unix timestamp of level start
 *   - {number} remainingLevelDays - Number of full days until level expiration (0 if expired)
 *   - {number} remainingLevelHours - Number of full hours until level expiration (0 if expired)
 *   - {number} remainingLevelMinutes - Number of minutes until level expiration (0 if expired)
 *   - {boolean} isExpireSoon - True if within the configured warning period before expiration
 *   - {boolean} isReachedHighestLevel - True if user has reached the maximum available level
 *   - {boolean} isLevelRetained - True if user's level status is retained
 *   - {boolean} isNewLevelThisMonth - True if user has just achieved a new level
 *   - {boolean} isLevelZero - True if user is at level 0 (no active level)
 *   - {boolean} isLevelExpireThisMonth - True if level expires within the current calendar month
 *   - {boolean} isLevelRetainedAndReachedHighestLevel - True if user has both retained status and reached max level
 */
const useUserLevelTimeRelatedData = () => {
  const levelData =
    useSelector(state => getMeData(state, 'level')) || EMPTY_OBJECT;
  const vipLevelInformExpireSoonDays =
    useSelector(state =>
      getRemoteConfigData(state, VIP_LEVEL_INFORM_EXPIRE_SOON_DAYS)
    ) || 0;
  const timestampOffsetSeconds =
    useSelector(state => getTimestampOffset(state, Accuracy.SECOND)) || 0;

  const [nowUnixTimestamp, setNowUnixTimestamp] = useState(
    getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    })
  );
  const level = getCurrentUserLevel({
    levelData,
    unixTimestamp: nowUnixTimestamp,
  });
  const { vipBenefitLevel } = useSelector(state =>
    getVipBenefitLevelThresholdByUserLevel(state, level)
  );
  const userLevelBenefitThresholdList = useSelector(state =>
    getUserLevelBenefitThresholdList(state)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setNowUnixTimestamp(
        getCurrentUnixTimestamp({
          offsetSeconds: timestampOffsetSeconds,
        })
      );
    }, INTERVAL_TIME);
    return () => {
      clearInterval(interval);
    };
  }, [timestampOffsetSeconds]);

  const expireUnixTimestamp = levelData?.[level]?.exp;
  const startUnixTimestamp = levelData?.[level]?.nbf;
  const maxLevel = userLevelBenefitThresholdList.at(-1)?.max ?? null;
  const isReachedHighestLevel = maxLevel === level;
  const isLevelZero = 0 === level;

  const isLevelExpireThisMonth = isSameMonth(
    fromUnixTime(nowUnixTimestamp),
    fromUnixTime(expireUnixTimestamp)
  );
  const isLevelStartThisMonth = isSameMonth(
    fromUnixTime(nowUnixTimestamp),
    fromUnixTime(startUnixTimestamp)
  );
  const isLevelRetained =
    !isLevelZero && !isLevelStartThisMonth && !isLevelExpireThisMonth;
  const isLevelRetainedAndReachedHighestLevel =
    (isLevelRetained || isLevelStartThisMonth) && isReachedHighestLevel;

  const { remainingLevelDays, remainingLevelHours, remainingLevelMinutes } =
    useMemo(() => {
      const diffInSeconds = Math.max(expireUnixTimestamp - nowUnixTimestamp, 0);
      const days = Math.floor(diffInSeconds / (24 * 60 * 60));
      const hours = Math.floor((diffInSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((diffInSeconds % (60 * 60)) / 60);
      return {
        remainingLevelDays: days,
        remainingLevelHours: hours,
        remainingLevelMinutes: minutes,
      };
    }, [expireUnixTimestamp, nowUnixTimestamp]);

  const isExpireSoon = useMemo(() => {
    let isExpireSoon = false;

    if (expireUnixTimestamp) {
      isExpireSoon = isWithinInterval(fromUnixTime(nowUnixTimestamp), {
        start: subDays(
          fromUnixTime(expireUnixTimestamp),
          vipLevelInformExpireSoonDays
        ),
        end: fromUnixTime(expireUnixTimestamp),
      });
    }

    return isExpireSoon;
  }, [expireUnixTimestamp, nowUnixTimestamp, vipLevelInformExpireSoonDays]);

  const result = useMemo(
    () => ({
      level,
      expireUnixTimestamp,
      startUnixTimestamp,
      remainingLevelDays,
      remainingLevelHours,
      remainingLevelMinutes,
      isExpireSoon,
      isReachedHighestLevel,
      isLevelRetained,
      isLevelZero,
      isLevelStartThisMonth,
      isLevelExpireThisMonth,
      isLevelRetainedAndReachedHighestLevel,
      vipBenefitLevel,
      maxLevel,
    }),
    [
      level,
      expireUnixTimestamp,
      startUnixTimestamp,
      remainingLevelDays,
      remainingLevelHours,
      remainingLevelMinutes,
      isExpireSoon,
      isReachedHighestLevel,
      isLevelRetained,
      isLevelZero,
      isLevelStartThisMonth,
      isLevelExpireThisMonth,
      isLevelRetainedAndReachedHighestLevel,
      vipBenefitLevel,
      maxLevel,
    ]
  );

  return result;
};

export default useUserLevelTimeRelatedData;
